import type { BillingConfig, BillingConfigStatus } from "./types";

export const BILLING_CONFIGURATIONS_QUERY_KEY = [
  "billing-configurations",
] as const;

export interface BillingConfigurationApiItem {
  amount?: string | number;
  currency?: string;
  hsn_sac?: string;
  qty?: number;
  cgst?: string | number;
  sgst?: string | number;
  igst?: string | number;
  billing_type?: string;
  frequency?: string;
  binning_start_date?: string;
}

export interface BillingConfigurationApiRecord {
  id?: string;
  invoice_number?: string;
  billing_type?: string;
  frequency?: string;
  binning_start_date?: string;
  items?: BillingConfigurationApiItem[];
  client_billing_email?: string;
  cc_emails?: string[];
  accountant_review_email?: string;
  reminder?: number;
  grace_period?: number;
  next_reminder_date?: string;
  notes?: string;
  project?: string;
  client_name?: string;
  status?: string;
  created_on?: string;
  updated_on?: string;
}

function parseAmount(value?: string | number): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseRate(value?: string | number): number {
  return parseAmount(value);
}

function formatGstRatePercent(rate: number): string {
  if (rate <= 0) {
    return "—";
  }

  if (Number.isInteger(rate)) {
    return `${rate}%`;
  }

  return `${parseFloat(rate.toFixed(2))}%`;
}

function normalizeStatus(status?: string): BillingConfigStatus {
  const value = status?.trim().toLowerCase() ?? "";

  if (!value) {
    return "Active";
  }

  if (value === "active") {
    return "Active";
  }

  if (value === "on hold" || value === "on_hold" || value === "onhold") {
    return "On Hold";
  }

  return "Draft";
}

function getGstRates(firstItem?: BillingConfigurationApiItem): {
  cgstRate: string;
  sgstRate: string;
  igstRate: string;
} {
  if (!firstItem) {
    return { cgstRate: "—", sgstRate: "—", igstRate: "—" };
  }

  const igst = parseRate(firstItem.igst);
  const cgst = parseRate(firstItem.cgst);
  const sgst = parseRate(firstItem.sgst);

  if (igst > 0) {
    return {
      cgstRate: "—",
      sgstRate: "—",
      igstRate: formatGstRatePercent(igst),
    };
  }

  return {
    cgstRate: cgst > 0 ? formatGstRatePercent(cgst) : "—",
    sgstRate: sgst > 0 ? formatGstRatePercent(sgst) : "—",
    igstRate: "—",
  };
}

function getTotalAmount(items?: BillingConfigurationApiItem[]): number {
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }

  return items.reduce((sum, item) => sum + parseAmount(item.amount), 0);
}

export function parseBillingAmount(value?: string | number): number {
  return parseAmount(value);
}

export function formatBillingConfigurationItemGstRates(
  item: BillingConfigurationApiItem
): {
  cgstRate: string;
  sgstRate: string;
  igstRate: string;
} {
  return getGstRates(item);
}

export function getBillingConfigurationTotalAmount(
  record: BillingConfigurationApiRecord
): number {
  return getTotalAmount(record.items);
}

function displayText(value?: string): string {
  const trimmed = value?.trim();
  return trimmed ?? "—";
}

export function resolveProjectLabel(
  projectId: string | undefined,
  projectOptions: Array<{ value: string; label: string }>
): string {
  const trimmed = projectId?.trim();
  if (!trimmed) {
    return "—";
  }

  return (
    projectOptions.find(option => option.value === trimmed)?.label ?? trimmed
  );
}

export function normalizeBillingConfigurationRecord(
  record: BillingConfigurationApiRecord
): BillingConfig {
  const rawId = record.id?.trim() ?? "";
  const firstItem = record.items?.[0];
  const gstRates = getGstRates(firstItem);

  return {
    rawId: rawId || undefined,
    project: displayText(record.project),
    client: displayText(record.client_name),
    type: displayText(firstItem?.billing_type ?? record.billing_type),
    frequency: displayText(firstItem?.frequency ?? record.frequency),
    amount: getTotalAmount(record.items),
    cgstRate: gstRates.cgstRate,
    sgstRate: gstRates.sgstRate,
    igstRate: gstRates.igstRate,
    status: normalizeStatus(record.status),
  };
}

export function normalizeBillingConfigurationList(
  response: unknown
): BillingConfig[] {
  if (!response) {
    return [];
  }

  const list = Array.isArray(response)
    ? response
    : ((response as { data?: unknown[] })?.data ?? []);

  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .filter(item => item && typeof item === "object")
    .map(item =>
      normalizeBillingConfigurationRecord(item as BillingConfigurationApiRecord)
    );
}
